/**
 * iter 73.14 — Strike 2 Phase A: Dispatch Command Center.
 *
 * Three-zone resizable dashboard for real-time dispatch:
 *
 *   ┌───────────┬──────────────────────┬────────────┐
 *   │ Job Queue │ Google Map           │ Drivers    │
 *   │ by status │ (job pins + meeting  │ (status,   │
 *   │ (left)    │  markers + due chip) │  battery)  │
 *   │           │                      │            │
 *   └───────────┴──────────────────────┴────────────┘
 *
 * Data:
 *   • Jobs      — GET /api/command-center/jobs
 *   • Drivers   — GET /api/command-center/drivers
 *   • Realtime  — postgres_changes on jobs + users + locations (org-scoped)
 *
 * Phase A ships:
 *   • Layout skeleton (resizable via react-resizable-panels)
 *   • Job pins + meeting handshake icons for `is_setup_meeting`
 *   • Due-within-N-days floating filter chip
 *   • White-labeled accent from `org.brand_primary_color`
 *   • Real-time job list refresh via Supabase Realtime
 *   • Driver list (Team Live style) with status dots + battery
 *
 * Deferred to Phase B/C: polygon lasso, proximity re-ranking, driver
 * waypoint markers on the map, drag-and-drop route builder, push
 * notifications.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import {
  MapTrifold, ListDashes, UsersThree, MagnifyingGlass, Clock, Handshake,
  BatteryFull, BatteryMedium, BatteryLow, BatteryEmpty, ArrowsClockwise,
  Polygon as PolygonIcon, X as XIcon, MapPin, ClockCounterClockwise,
  Eye, EyeSlash, CalendarBlank as CalIcon,
} from "@phosphor-icons/react";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";

// iter 73.15 Phase H — SVG path lookup for map_settings.icon values.
// Kept in sync with MAP_ICON_LIBRARY in MapDesignSection.jsx. Paths are
// authored with coordinates in the ±14 range and `scale: 0.7` so they
// render with roughly the same visual weight as the default CIRCLE
// symbol (scale 8, ~16px diameter).
const ICON_PATHS = {
  circle:    { path: null,                                          scale: 8    }, // uses SymbolPath.CIRCLE
  square:    { path: "M -10,-10 L 10,-10 L 10,10 L -10,10 Z",       scale: 0.9  },
  diamond:   { path: "M 0,-14 L 12,-6 L 0,6 L -12,-6 Z",            scale: 0.9  },
  pin:       { path: "M 0,-14 C -8,-14 -12,-8 -12,-2 C -12,6 0,14 0,14 C 0,14 12,6 12,-2 C 12,-8 8,-14 0,-14 Z", scale: 0.9 },
  star:      { path: "M 0,-14 L 4,-4 L 14,-4 L 6,2 L 9,12 L 0,6 L -9,12 L -6,2 L -14,-4 L -4,-4 Z", scale: 0.7 },
  flag:      { path: "M -8,-14 L -8,14 M -8,-14 L 10,-14 L 6,-8 L 10,-2 L -8,-2 Z", scale: 0.9 },
  handshake: { path: "M 0,-14 L 12,-6 L 0,6 L -12,-6 Z",            scale: 1    }, // reuse diamond glyph for meeting
};

import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { loadGoogleMaps } from "../lib/useGoogleMaps";
import RouteBuilder from "./CommandCenter.RouteBuilder";

const STATUS_COLORS = {
  Active:     "#22c55e",
  "On Break": "#f59e0b",
  Busy:       "#ef4444",
  Offline:    "#71717a",
};
const STATUS_RANK = { Active: 0, "On Break": 1, Busy: 2, Offline: 3 };

function BatteryIcon({ level }) {
  if (level == null) return null;
  const Icon = level >= 60 ? BatteryFull : level >= 30 ? BatteryMedium : level >= 10 ? BatteryLow : BatteryEmpty;
  const color = level >= 30 ? "text-muted-foreground" : level >= 10 ? "text-amber-500" : "text-destructive";
  return <Icon size={11} className={color} />;
}

// ---------------------------------------------------------------------------
// LEFT PANEL — Job Queue grouped by status
// ---------------------------------------------------------------------------
function JobQueue({ jobs, selectedId, onSelect, onAddToRoute, primary, mapSettings, onBulkAddToRoute }) {
  // iter 73.15 Phase F — multi-select. `checked` is a Set of job ids.
  // Bulk-add posts them all to the RouteBuilder via `onBulkAddToRoute`.
  const [checked, setChecked] = useState(() => new Set());
  const toggleChecked = (id, e) => {
    e?.stopPropagation();
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearChecked = () => setChecked(new Set());
  const bulkAdd = () => {
    const rows = jobs.filter((j) => checked.has(j.id) && j.latitude != null && j.longitude != null);
    onBulkAddToRoute?.(rows);
    clearChecked();
  };

  // Group jobs by the 5 canonical dispatcher buckets per Strike 2 spec:
  // Unassigned → Ready → Assigned → In Progress → Exception. Everything
  // else falls into a "Other" bucket at the bottom. Buckets we can't map
  // (no assigned_to + no status match) default to Unassigned.
  const grouped = useMemo(() => {
    const buckets = {
      Unassigned: [], Ready: [], Assigned: [], "In Progress": [], Exception: [], Other: [],
    };
    for (const j of jobs) {
      const s = (j.status_name || "").toLowerCase();
      if (s.includes("exception") || s.includes("failed") || s.includes("error")) buckets.Exception.push(j);
      else if (s.includes("in progress") || s.includes("in-progress") || s.includes("progress")) buckets["In Progress"].push(j);
      else if (s.includes("assigned") || j.assigned_to_id) buckets.Assigned.push(j);
      else if (s.includes("ready") || s.includes("scheduled") || s.includes("dispatched")) buckets.Ready.push(j);
      else if (!s || s.includes("unassigned") || s.includes("new") || s.includes("intake")) buckets.Unassigned.push(j);
      else buckets.Other.push(j);
    }
    return Object.entries(buckets).filter(([, rows]) => rows.length > 0);
  }, [jobs]);

  const total = jobs.length;
  const anyChecked = checked.size > 0;

  return (
    <div className="h-full flex flex-col bg-card">
      <div
        className="px-3 py-2 border-b border-border flex items-center gap-2 text-[11px] mono uppercase text-muted-foreground tracking-wide"
        style={{ borderTopColor: primary, borderTopWidth: 2 }}
      >
        <ListDashes size={12} /> JOB QUEUE ({total})
      </div>
      {anyChecked && (
        <div
          className="px-3 py-1.5 bg-primary/10 border-b border-primary/40 flex items-center justify-between gap-2 text-xs"
          data-testid="cc-job-queue-bulk-bar"
        >
          <span className="mono font-semibold" style={{ color: primary }}>
            {checked.size} selected
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={bulkAdd}
              className="px-2 py-0.5 rounded bg-primary text-primary-foreground text-[11px] font-semibold hover:opacity-90"
              data-testid="cc-job-queue-bulk-add"
              title={`Add all ${checked.size} selected jobs to the current route`}
            >+ Route</button>
            <button
              onClick={clearChecked}
              className="px-2 py-0.5 rounded border border-border text-[11px] hover:bg-muted"
              data-testid="cc-job-queue-bulk-clear"
              title="Clear selection"
            >×</button>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto" data-testid="cc-job-queue">
        {grouped.length === 0 && (
          <div className="text-xs text-muted-foreground italic p-3 text-center">
            No active jobs.
          </div>
        )}
        {grouped.map(([status, rows]) => (
          <div key={status} className="border-b border-border">
            <div className="px-3 py-1.5 bg-muted/30 text-[10px] mono uppercase tracking-wide flex items-center justify-between">
              <span>{status}</span>
              <span className="text-muted-foreground">{rows.length}</span>
            </div>
            {rows.map((j) => {
              const sel = j.id === selectedId;
              const isChecked = checked.has(j.id);
              const statusOverride = mapSettings?.statuses?.[j.status_id]?.color;
              const swatch = statusOverride || (j.is_setup_meeting ? (mapSettings?.meeting?.color || "#8b5cf6") : null);
              return (
                <div
                  key={j.id}
                  onClick={() => onSelect(j)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") onSelect(j); }}
                  className={`w-full text-left px-3 py-1.5 border-l-2 hover:bg-muted/40 transition-colors group cursor-pointer ${
                    sel ? "bg-muted/60" : ""
                  }`}
                  style={{ borderLeftColor: sel ? primary : "transparent" }}
                  data-testid={`cc-job-queue-item-${j.id}`}
                >
                  <div className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => toggleChecked(j.id, e)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-3 w-3 cursor-pointer accent-primary"
                      data-testid={`cc-job-queue-check-${j.id}`}
                      aria-label={`Select job ${j.job_number || j.id}`}
                    />
                    {swatch && (
                      <span
                        className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: swatch }}
                        title="Status color"
                      />
                    )}
                    {j.is_setup_meeting && (
                      <Handshake size={11} weight="fill" className="text-purple-500 flex-shrink-0" title="Setup meeting" />
                    )}
                    <span className="text-xs font-medium truncate flex-1">
                      {j.job_number || j.id.slice(0, 8)}
                    </span>
                    {j.due_date && (
                      <span className="text-[10px] text-muted-foreground mono flex-shrink-0">
                        {j.due_date}
                      </span>
                    )}
                    {/* Add-to-route button appears on hover — feeds RouteBuilder */}
                    {j.latitude != null && j.longitude != null && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-muted-foreground hover:text-primary"
                        onClick={(e) => { e.stopPropagation(); onAddToRoute?.(j); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onAddToRoute?.(j); } }}
                        title="Add to current route"
                        data-testid={`cc-job-queue-add-${j.id}`}
                      >+</span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate pl-5">
                    {j.location_name || j.address_line1 || "—"}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RIGHT PANEL — Drivers
// ---------------------------------------------------------------------------
function DriverList({ drivers, query, setQuery, primary, proximityJob, onExitProximity }) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const arr = q
      ? drivers.filter((d) =>
          (d.name || "").toLowerCase().includes(q) ||
          (d.email || "").toLowerCase().includes(q))
      : drivers;
    // In proximity mode, respect the server-provided order (distance asc).
    if (proximityJob) return arr;
    return [...arr].sort((a, b) => {
      const ra = STATUS_RANK[a.current_status] ?? 99;
      const rb = STATUS_RANK[b.current_status] ?? 99;
      if (ra !== rb) return ra - rb;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [drivers, query, proximityJob]);

  return (
    <div className="h-full flex flex-col bg-card">
      <div
        className="px-3 py-2 border-b border-border flex items-center gap-2 text-[11px] mono uppercase text-muted-foreground tracking-wide"
        style={{ borderTopColor: primary, borderTopWidth: 2 }}
      >
        <UsersThree size={12} />
        {proximityJob ? "NEAREST TO JOB" : "ACTIVE SERVERS"} ({drivers.length})
      </div>

      {/* Proximity-mode banner */}
      {proximityJob && (
        <div
          className="px-3 py-1.5 border-b border-border bg-amber-500/10 flex items-center justify-between text-[11px]"
          data-testid="cc-driver-proximity-banner"
        >
          <span className="truncate">
            Ranked by distance to <strong>{proximityJob.job_number || proximityJob.id.slice(0, 8)}</strong>
          </span>
          <button
            onClick={onExitProximity}
            className="ml-2 text-amber-700 dark:text-amber-300 hover:opacity-80"
            title="Return to status ranking"
            data-testid="cc-driver-proximity-exit"
          ><XIcon size={11} weight="bold" /></button>
        </div>
      )}

      <div className="px-3 py-2 border-b border-border">
        <div className="relative">
          <MagnifyingGlass size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            className="input text-xs pl-6"
            placeholder="Search servers…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="cc-driver-search"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="cc-driver-list">
        {filtered.map((d) => {
          const st = d.current_status || "Offline";
          const dist = d.distance_miles;
          return (
            <div
              key={d.id}
              className="flex items-center gap-2 px-3 py-1.5 border-b border-border hover:bg-muted/40 transition-colors"
              data-testid={`cc-driver-row-${d.id}`}
              title={`${d.name} — ${st}${dist != null ? ` — ${dist} mi` : ""}`}
            >
              <div className="relative flex-shrink-0">
                {d.avatar_url ? (
                  <img src={d.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover bg-muted" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-muted text-[10px] font-semibold flex items-center justify-center">
                    {(d.name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?"}
                  </div>
                )}
                <span
                  className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card"
                  style={{ background: STATUS_COLORS[st] || STATUS_COLORS.Offline }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs truncate leading-tight">{d.name}</div>
                <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1">
                  {st}
                  {d.battery_level != null && <>· <BatteryIcon level={d.battery_level} /> {d.battery_level}%</>}
                  {d.open_job_count > 0 && <>· {d.open_job_count} job{d.open_job_count === 1 ? "" : "s"}</>}
                </div>
              </div>
              {/* iter 73.14 — Distance chip in proximity mode. */}
              {proximityJob && dist != null && (
                <span
                  className="text-[10px] mono px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold flex-shrink-0"
                  data-testid={`cc-driver-distance-${d.id}`}
                >{dist} mi</span>
              )}
              {proximityJob && dist == null && (
                <span className="text-[9px] mono text-muted-foreground italic flex-shrink-0">no gps</span>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-xs text-muted-foreground italic p-3 text-center">
            {query ? "No matches." : "No drivers yet."}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CENTER PANEL — Google Map with job pins + driver avatars + lasso
// ---------------------------------------------------------------------------
// iter 73.16 — Small helper that lets the user assemble one date filter
// (type + start + end) then push it into the parent's dateFilters array.
// Auto-collapses after the entry is added, so successive adds require
// re-opening the popover — this keeps the toolbar visually calm.
function DateFilterBuilder({ onAdd, onDone }) {
  const [type, setType] = useState("R");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const commit = () => {
    if (!start && !end) return; // ignore empty
    onAdd({ type, start: start || null, end: end || null });
    setStart(""); setEnd("");
    onDone?.();
  };
  return (
    <div
      className="flex items-center gap-1.5 bg-card/95 backdrop-blur border border-border rounded-full px-3 py-1.5 shadow-sm text-xs"
      data-testid="cc-datefilter-builder"
    >
      {[
        ["R", "Received"], ["C", "Created"], ["A", "Action"], ["H", "Hard Due"], ["E", "Expiration"],
      ].map(([k, label]) => (
        <button
          key={k}
          onClick={() => setType(k)}
          className={`px-1.5 py-0.5 rounded text-[10px] font-semibold mono ${
            type === k ? "bg-blue-500 text-white" : "hover:bg-muted"
          }`}
          title={label}
          data-testid={`cc-datefilter-builder-${k}`}
        >{k}</button>
      ))}
      <input
        type="date"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        className="bg-transparent border-b border-border outline-none mono text-xs w-[110px] ml-1"
        data-testid="cc-datefilter-builder-start"
      />
      <span className="text-muted-foreground">→</span>
      <input
        type="date"
        value={end}
        onChange={(e) => setEnd(e.target.value)}
        className="bg-transparent border-b border-border outline-none mono text-xs w-[110px]"
        data-testid="cc-datefilter-builder-end"
      />
      <button
        onClick={commit}
        disabled={!start && !end}
        className="px-1.5 py-0.5 rounded bg-blue-500 text-white text-[10px] font-semibold mono disabled:opacity-40"
        data-testid="cc-datefilter-builder-add"
        title="Add this filter to the stack"
      >ADD</button>
      <button
        onClick={onDone}
        className="ml-1 text-muted-foreground hover:text-foreground text-[10px] mono"
        title="Close"
      >×</button>
    </div>
  );
}


function MapPanel({
  jobs, drivers, selectedId, onSelectJob,
  dueWithin, setDueWithin, primary,
  onLassoSelect, lassoSelectedIds, onClearLasso,
  mapSettings,
  // iter 73.15 Phase E — Time-Machine / date-engine / driver-visibility.
  mode, setMode,
  dateFilters, setDateFilters,
  histStart, setHistStart, histEnd, setHistEnd,
  lastXJobs, setLastXJobs,
  showDrivers, setShowDrivers,
  onBoundsChanged,
  pinCount,
  ghostPin, onSetGhostPin,
  nextStopBreachIds,
}) {
  const mapDiv = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);           // job pins
  const driverMarkersRef = useRef([]);     // driver avatars
  const clustererRef = useRef(null);       // MarkerClusterer instance (>300 pins)
  const boundsDebounceRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  // iter 73.14 Phase B — manual lasso (Google deprecated DrawingManager
  // in v3.65+). We collect click-positions on the map and draw a live
  // polygon that the user can close via the toolbar button.
  const [lassoActive, setLassoActive] = useState(false);
  const lassoPathRef = useRef([]);            // {lat, lng}[]
  const lassoOverlaysRef = useRef({ line: null, poly: null, vertices: [] });
  const mapClickListenerRef = useRef(null);
  const [lassoVertexCount, setLassoVertexCount] = useState(0);
  const [showDateFilter, setShowDateFilter] = useState(false);

  // Boot the map once.
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !mapDiv.current) return;
        mapRef.current = new google.maps.Map(mapDiv.current, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 4,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          styles: [
            { featureType: "poi.business", stylers: [{ visibility: "off" }] },
            { featureType: "poi.medical",  stylers: [{ visibility: "off" }] },
            { featureType: "transit",      stylers: [{ visibility: "off" }] },
          ],
        });
        setReady(true);
        // iter 73.15 Phase E — debounced bounds change → parent refetch.
        mapRef.current.addListener("idle", () => {
          const b = mapRef.current.getBounds();
          if (!b || !onBoundsChanged) return;
          clearTimeout(boundsDebounceRef.current);
          boundsDebounceRef.current = setTimeout(() => {
            const ne = b.getNorthEast();
            const sw = b.getSouthWest();
            onBoundsChanged({
              ne_lat: ne.lat(), ne_lng: ne.lng(),
              sw_lat: sw.lat(), sw_lng: sw.lng(),
            });
          }, 500);
        });
      })
      .catch((e) => setError(String(e)));
    return () => { cancelled = true; clearTimeout(boundsDebounceRef.current); };
  }, []); // onBoundsChanged captured via closure — parent memos it

  // Helper — wipe lasso overlays.
  const _clearLassoOverlays = () => {
    const o = lassoOverlaysRef.current;
    if (o.line) o.line.setMap(null);
    if (o.poly) o.poly.setMap(null);
    o.vertices.forEach((m) => m.setMap(null));
    lassoOverlaysRef.current = { line: null, poly: null, vertices: [] };
  };

  const closeLasso = () => {
    if (!window.google?.maps?.geometry || lassoPathRef.current.length < 3) {
      // Nothing to close.
      setLassoActive(false);
      if (mapClickListenerRef.current) window.google.maps.event.removeListener(mapClickListenerRef.current);
      mapClickListenerRef.current = null;
      return;
    }
    const google = window.google;
    // Remove live-polyline; draw the final polygon.
    if (lassoOverlaysRef.current.line) lassoOverlaysRef.current.line.setMap(null);
    lassoOverlaysRef.current.line = null;
    const poly = new google.maps.Polygon({
      paths: lassoPathRef.current,
      fillColor: primary || "#3b82f6",
      fillOpacity: 0.15,
      strokeColor: primary || "#3b82f6",
      strokeWeight: 2,
      clickable: false,
      map: mapRef.current,
    });
    lassoOverlaysRef.current.poly = poly;

    // Detach map click listener; lasso stops accepting new vertices.
    if (mapClickListenerRef.current) {
      google.maps.event.removeListener(mapClickListenerRef.current);
      mapClickListenerRef.current = null;
    }
    setLassoActive(false);

    // Hit-test every job pin against the polygon.
    const inside = markersRef.current
      .filter((m) => google.maps.geometry.poly.containsLocation(m.getPosition(), poly))
      .map((m) => m.__jobId)
      .filter(Boolean);
    onLassoSelect(inside);
  };

  const toggleLasso = () => {
    if (!ready || !window.google?.maps) return;
    const google = window.google;
    if (lassoActive) {
      // In-progress — treat click as "close polygon".
      closeLasso();
      return;
    }
    // Start a new lasso — clear any prior selection + overlays.
    _clearLassoOverlays();
    onClearLasso();
    lassoPathRef.current = [];
    setLassoVertexCount(0);
    setLassoActive(true);

    // Attach a temporary click listener that accretes vertices.
    mapClickListenerRef.current = mapRef.current.addListener("click", (ev) => {
      const pt = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
      lassoPathRef.current.push(pt);
      setLassoVertexCount(lassoPathRef.current.length);
      // Drop a small vertex dot.
      const dot = new google.maps.Marker({
        position: pt, map: mapRef.current,
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 4, fillColor: primary || "#3b82f6", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 1 },
        clickable: false, zIndex: 800,
      });
      lassoOverlaysRef.current.vertices.push(dot);
      // Rebuild the live polyline.
      if (lassoOverlaysRef.current.line) lassoOverlaysRef.current.line.setMap(null);
      lassoOverlaysRef.current.line = new google.maps.Polyline({
        path: lassoPathRef.current,
        strokeColor: primary || "#3b82f6",
        strokeWeight: 2,
        strokeOpacity: 0.7,
        clickable: false,
        map: mapRef.current,
      });
    });
  };

  const clearLasso = () => {
    _clearLassoOverlays();
    lassoPathRef.current = [];
    setLassoVertexCount(0);
    if (mapClickListenerRef.current && window.google?.maps) {
      window.google.maps.event.removeListener(mapClickListenerRef.current);
      mapClickListenerRef.current = null;
    }
    setLassoActive(false);
    onClearLasso();
  };

  // Re-render JOB markers.
  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return;
    const google = window.google;
    // Tear down clusterer first (it holds refs to old markers).
    if (clustererRef.current) { clustererRef.current.clearMarkers(); clustererRef.current = null; }
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const bounds = new google.maps.LatLngBounds();
    let placed = 0;
    const inLasso = new Set(lassoSelectedIds || []);

    // iter 73.15 Phase H+ — Collision-detection spider layout.
    // Group jobs whose (lat, lng) collapse to the same 5-decimal key
    // (~1.1m grid). Singletons render at their true coordinate.
    // Duplicates get fanned out in a small circle around the anchor
    // so dispatchers can visually distinguish jobs at a strip-mall.
    // Offset is a fixed ~11m radius (0.0001°) — imperceptible at
    // country zoom, tight but legible at street zoom.
    const FAN_DEG = 0.0001;
    const buckets = new Map();
    for (const j of jobs) {
      if (j.latitude == null || j.longitude == null) continue;
      const key = `${j.latitude.toFixed(5)}:${j.longitude.toFixed(5)}`;
      const arr = buckets.get(key) || [];
      arr.push(j);
      buckets.set(key, arr);
    }
    const posForJob = new Map();
    for (const [, group] of buckets) {
      if (group.length === 1) {
        posForJob.set(group[0].id, { lat: group[0].latitude, lng: group[0].longitude });
        continue;
      }
      const centerLat = group[0].latitude;
      const centerLng = group[0].longitude;
      // Lng grows faster than lat at low latitudes — compensate so the
      // fan is a visual circle, not an ellipse.
      const lngScale = 1 / Math.cos((centerLat * Math.PI) / 180 || 0.01);
      group.forEach((j, i) => {
        const a = (2 * Math.PI * i) / group.length - Math.PI / 2; // start at top
        posForJob.set(j.id, {
          lat: centerLat + Math.sin(a) * FAN_DEG,
          lng: centerLng + Math.cos(a) * FAN_DEG * lngScale,
        });
      });
    }

    for (const j of jobs) {
      if (j.latitude == null || j.longitude == null) continue;
      const pos = posForJob.get(j.id) || { lat: j.latitude, lng: j.longitude };
      const isMeeting = !!j.is_setup_meeting;
      const isLassoed = inLasso.has(j.id);
      // iter 73.15 Phase D/H — consume org map_settings (status/priority
      // overrides win over the default; meeting overrides everything).
      const statusOverride = mapSettings?.statuses?.[j.status_id] || {};
      const meetingOverride = mapSettings?.meeting || {};
      const defaultOverride = mapSettings?.default || {};
      const color =
        (isMeeting && meetingOverride.color) ||
        statusOverride.color ||
        defaultOverride.color ||
        primary ||
        "#3b82f6";
      const iconKey =
        (isMeeting && (meetingOverride.icon || "diamond")) ||
        statusOverride.icon ||
        defaultOverride.icon ||
        (isMeeting ? "diamond" : "circle");
      const shape = ICON_PATHS[iconKey] || ICON_PATHS.circle;
      const icon = {
        path: shape.path ?? google.maps.SymbolPath.CIRCLE,
        fillColor: color,
        fillOpacity: 0.95,
        strokeColor: isLassoed ? "#facc15" : "#fff",      // yellow ring for lassoed
        strokeWeight: isLassoed ? 4 : 2,
        scale: shape.scale,
      };
      const m = new google.maps.Marker({
        position: pos,
        map: null, // filled in by clusterer OR directly below
        title: `${j.job_number || j.id} — ${j.status_name || "Unassigned"}`,
        icon,
        // iter 73.15 Phase F — meeting pins pulse via Google Maps
        // animation.BOUNCE (native, no CSS needed). Non-meeting pins stay
        // static to keep the map calm.
        animation: isMeeting ? google.maps.Animation.BOUNCE : null,
        zIndex: j.id === selectedId ? 999 : (isLassoed ? 100 : (isMeeting ? 10 : 1)),
      });
      m.__jobId = j.id;
      m.addListener("click", () => onSelectJob(j));
      markersRef.current.push(m);
      bounds.extend(pos);
      placed++;
    }

    // iter 73.16 — Red pulsing breach ring behind pins flagged by the
    // parent's next-stop breach detector. Rendered as a *second* Marker
    // (SVG with SMIL animation) at the same coord, below the pin.
    for (const j of jobs) {
      if (j.latitude == null || j.longitude == null) continue;
      if (!nextStopBreachIds?.has(j.id)) continue;
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42">
          <circle cx="21" cy="21" r="15" fill="none" stroke="#dc2626" stroke-width="3">
            <animate attributeName="r" values="12;20;12" dur="1.4s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.9;0.2;0.9" dur="1.4s" repeatCount="indefinite"/>
          </circle>
        </svg>`;
      const url = "data:image/svg+xml;utf8," + encodeURIComponent(svg);
      const pos = posForJob.get(j.id) || { lat: j.latitude, lng: j.longitude };
      const ring = new google.maps.Marker({
        position: pos,
        map: mapRef.current,
        clickable: false,
        title: "Predicted breach — dispatcher intervention required",
        icon: { url, scaledSize: new google.maps.Size(42, 42), anchor: new google.maps.Point(21, 21) },
        zIndex: 200,
      });
      markersRef.current.push(ring);
    }

    // iter 73.15 Phase E — Marker clustering kicks in above 300 pins to
    // keep the map performant during historical time-machine queries.
    if (markersRef.current.length > 300) {
      clustererRef.current = new MarkerClusterer({
        map: mapRef.current,
        markers: markersRef.current,
        algorithm: new SuperClusterAlgorithm({ radius: 80, maxZoom: 15 }),
      });
    } else {
      markersRef.current.forEach((m) => m.setMap(mapRef.current));
    }

    if (placed > 1 && !lassoSelectedIds?.length && placed <= 300) mapRef.current.fitBounds(bounds, 80);
    else if (placed === 1) { mapRef.current.setCenter(bounds.getCenter()); mapRef.current.setZoom(11); }
  }, [jobs, ready, selectedId, onSelectJob, primary, lassoSelectedIds, mapSettings, nextStopBreachIds]);

  // Re-render DRIVER avatar markers.
  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return;
    const google = window.google;
    driverMarkersRef.current.forEach((m) => m.setMap(null));
    driverMarkersRef.current = [];
    // iter 73.15 Phase E — driver visibility toggle (independent from
    // time-machine mode). When off, we skip rendering entirely.
    if (!showDrivers) return;

    const now = Date.now();
    for (const d of drivers) {
      if (d.last_known_lat == null || d.last_known_lng == null) continue;
      const st = d.current_status || "Offline";
      const stroke = STATUS_COLORS[st] || STATUS_COLORS.Offline;
      const initials = (d.name || "?").split(/\s+/).slice(0, 2)
        .map((s) => s[0]?.toUpperCase()).join("") || "?";
      // iter 73.15 Phase F — "Signal Stale" warning if last update > 5min.
      const updatedAt = d.last_location_update_at ? new Date(d.last_location_update_at).getTime() : null;
      const stale = updatedAt ? (now - updatedAt) > (5 * 60 * 1000) : true;
      // iter 73.16 — Overstay = arrived_at > expected_duration_minutes ago.
      // Renders as a yellow pulsing outer ring.
      const arrivedMs = d.active_arrived_at ? new Date(d.active_arrived_at).getTime() : null;
      const expectedMin = d.active_expected_duration_minutes ?? 15;
      const overstay = arrivedMs != null && (now - arrivedMs) > expectedMin * 60000;
      const staleRing = stale ? '<circle cx="18" cy="18" r="17" fill="none" stroke="#f59e0b" stroke-width="2" stroke-dasharray="3 2"/>' : "";
      // Pulsing yellow overstay ring uses SMIL animation (supported in
      // all modern Chromium/Firefox; degrades to static in edge cases).
      const overstayRing = overstay
        ? `<circle cx="18" cy="18" r="16" fill="none" stroke="#eab308" stroke-width="3">
             <animate attributeName="r" values="14;19;14" dur="1.4s" repeatCount="indefinite"/>
             <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite"/>
           </circle>`
        : "";
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
          ${overstayRing}
          <circle cx="18" cy="18" r="14" fill="#0f172a" stroke="${stroke}" stroke-width="4"/>
          <text x="18" y="22" font-family="Inter, sans-serif" font-size="12" font-weight="700"
                text-anchor="middle" fill="#f8fafc">${initials}</text>
          ${staleRing}
        </svg>`;
      const url = "data:image/svg+xml;utf8," + encodeURIComponent(svg);
      const m = new google.maps.Marker({
        position: { lat: d.last_known_lat, lng: d.last_known_lng },
        map: mapRef.current,
        title: `${d.name} — ${st}${stale ? " · SIGNAL STALE" : ""}${overstay ? " · OVERSTAY" : ""}`,
        // iter 73.15 hotfix — anchor at (18, 36) instead of (18, 18) so
        // the avatar sits *above* the coordinate (like a speech bubble
        // tail) rather than centred on it — leaves the underlying job
        // pin visible when a driver is at the same lat/lng.
        icon: { url, scaledSize: new google.maps.Size(36, 36), anchor: new google.maps.Point(18, 36) },
        zIndex: 500,
      });
      driverMarkersRef.current.push(m);
    }
  }, [drivers, ready, showDrivers]);

  // iter 73.15 Phase F — Ghost Pin. Renders (and re-renders) whenever
  // `ghostPin` changes. A single persistent marker; recentering happens
  // when set (below in the search handler).
  const ghostMarkerRef = useRef(null);
  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return;
    const google = window.google;
    if (ghostMarkerRef.current) { ghostMarkerRef.current.setMap(null); ghostMarkerRef.current = null; }
    if (!ghostPin) return;
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
        <path d="M16 0C8 0 2 6 2 14c0 10 14 28 14 28s14-18 14-28c0-8-6-14-14-14z"
              fill="#f8fafc" stroke="#0f172a" stroke-width="2" stroke-dasharray="3 2" opacity="0.9"/>
        <circle cx="16" cy="14" r="5" fill="#0f172a"/>
      </svg>`;
    const url = "data:image/svg+xml;utf8," + encodeURIComponent(svg);
    ghostMarkerRef.current = new google.maps.Marker({
      position: { lat: ghostPin.lat, lng: ghostPin.lng },
      map: mapRef.current,
      title: `Ghost pin: ${ghostPin.label || ""}`,
      icon: { url, scaledSize: new google.maps.Size(32, 42), anchor: new google.maps.Point(16, 42) },
      zIndex: 800,
    });
    // Auto-recenter when a fresh ghost is dropped.
    mapRef.current.panTo({ lat: ghostPin.lat, lng: ghostPin.lng });
    if (mapRef.current.getZoom() < 12) mapRef.current.setZoom(13);
  }, [ghostPin, ready]);

  // iter 73.15 Phase F — Global Location Search input state.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchDebounce = useRef(null);
  useEffect(() => {
    clearTimeout(searchDebounce.current);
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    searchDebounce.current = setTimeout(async () => {
      try {
        // Piggyback on existing /api/locations?q=... (Phase 2 endpoint).
        const r = await api.get(`/locations?q=${encodeURIComponent(searchQuery)}&limit=8`);
        setSearchResults(Array.isArray(r.data) ? r.data.filter((l) => l.latitude != null && l.longitude != null) : []);
      } catch { setSearchResults([]); }
    }, 250);
    return () => clearTimeout(searchDebounce.current);
  }, [searchQuery]);

  return (
    <div className="h-full relative bg-muted/20" data-testid="cc-map-panel">
      <div ref={mapDiv} className="absolute inset-0" data-testid="cc-map" />

      {error && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-destructive p-4">
          Failed to load Google Maps: {error}
        </div>
      )}

      {/* iter 73.15 Phase F — Global Location Search + Ghost Pin */}
      <div className="absolute top-3 right-3 z-[5] w-64" data-testid="cc-location-search-wrap">
        <div className="relative">
          <div className="flex items-center gap-2 bg-card/95 backdrop-blur border border-border rounded-full px-3 py-1.5 shadow-sm text-xs">
            <MagnifyingGlass size={12} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Search directory…"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
              className="flex-1 bg-transparent outline-none text-xs"
              data-testid="cc-location-search-input"
            />
            {ghostPin && (
              <button
                onClick={(e) => { e.stopPropagation(); onSetGhostPin(null); }}
                className="text-muted-foreground hover:text-destructive text-[10px] mono"
                title="Remove ghost pin"
                data-testid="cc-ghost-pin-clear"
              >GHOST ×</button>
            )}
          </div>
          {searchOpen && searchResults.length > 0 && (
            <div
              className="absolute top-full mt-1 left-0 right-0 bg-card border border-border rounded shadow-lg overflow-hidden z-20 max-h-64 overflow-y-auto"
              data-testid="cc-location-search-results"
            >
              {searchResults.map((loc) => (
                <button
                  key={loc.id}
                  onClick={() => {
                    onSetGhostPin({ lat: loc.latitude, lng: loc.longitude, label: loc.name });
                    setSearchOpen(false);
                    setSearchQuery(loc.name);
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-b border-border/40 flex items-start gap-2"
                  data-testid={`cc-location-search-result-${loc.id}`}
                >
                  <MapPin size={11} className="text-primary flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{loc.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {loc.address1 || ""} {loc.city ? `· ${loc.city}` : ""} {loc.state ? `, ${loc.state}` : ""}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top-left toolbar: due-within chip + lasso toggle + time-machine + date-engine */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2 flex-wrap max-w-[calc(100%-18rem)]">
        <div className="flex items-center gap-2 bg-card/95 backdrop-blur border border-border rounded-full px-3 py-1.5 shadow-sm text-xs" data-testid="cc-due-filter">
          <Clock size={12} className="text-muted-foreground" />
          <span>Due within</span>
          <input
            type="number" min={0} max={365}
            value={dueWithin ?? ""}
            onChange={(e) => setDueWithin(e.target.value === "" ? null : Math.max(0, Number(e.target.value)))}
            placeholder="—"
            className="w-12 bg-transparent border-b border-border focus:border-primary outline-none text-center mono"
            data-testid="cc-due-filter-input"
          />
          <span>days</span>
          {dueWithin != null && (
            <button
              onClick={() => setDueWithin(null)}
              className="text-muted-foreground hover:text-foreground text-[10px] mono ml-1"
              data-testid="cc-due-filter-clear" title="Clear filter"
            >CLEAR</button>
          )}
        </div>
        <button
          type="button"
          onClick={toggleLasso}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs shadow-sm border transition-colors ${
            lassoActive
              ? "bg-amber-500 border-amber-600 text-white"
              : "bg-card/95 backdrop-blur border-border hover:bg-muted"
          }`}
          title={
            lassoActive
              ? (lassoVertexCount >= 3 ? "Close polygon and select jobs inside" : "Click 3+ points on the map, then click here to close")
              : "Click to draw a boundary polygon (click points on the map)"
          }
          data-testid="cc-lasso-toggle"
          aria-pressed={lassoActive}
        >
          <PolygonIcon size={12} />
          {lassoActive
            ? (lassoVertexCount >= 3 ? `Close (${lassoVertexCount} pts)` : `Drawing (${lassoVertexCount}/3)…`)
            : "Lasso"}
        </button>

        {/* iter 73.15 Phase E — Time-Machine toggle (Active | Historical) */}
        <div
          className="flex items-center bg-card/95 backdrop-blur border border-border rounded-full shadow-sm text-xs overflow-hidden"
          data-testid="cc-timemachine"
        >
          <button
            type="button"
            onClick={() => setMode("active")}
            className={`px-2.5 py-1.5 flex items-center gap-1 ${mode === "active" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            data-testid="cc-timemachine-active"
          >
            <MapPin size={11} weight={mode === "active" ? "bold" : "regular"} /> Active
          </button>
          <button
            type="button"
            onClick={() => setMode("historical")}
            className={`px-2.5 py-1.5 flex items-center gap-1 border-l border-border ${mode === "historical" ? "bg-amber-500 text-white border-amber-600" : "hover:bg-muted"}`}
            data-testid="cc-timemachine-historical"
          >
            <ClockCounterClockwise size={11} weight={mode === "historical" ? "bold" : "regular"} /> Historical
          </button>
        </div>

        {/* Historical toolbar — visible only when mode='historical' */}
        {mode === "historical" && (
          <div
            className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/40 rounded-full px-3 py-1.5 shadow-sm text-xs"
            data-testid="cc-historical-toolbar"
          >
            <CalIcon size={11} className="text-amber-700 dark:text-amber-300" />
            <input
              type="date"
              value={histStart || ""}
              onChange={(e) => setHistStart(e.target.value || null)}
              className="bg-transparent outline-none text-xs mono w-[110px]"
              data-testid="cc-hist-start"
            />
            <span className="text-muted-foreground">→</span>
            <input
              type="date"
              value={histEnd || ""}
              onChange={(e) => setHistEnd(e.target.value || null)}
              className="bg-transparent outline-none text-xs mono w-[110px]"
              data-testid="cc-hist-end"
            />
            <span className="text-muted-foreground border-l border-amber-500/40 pl-2 ml-1">Last</span>
            <input
              type="number"
              min={1} max={5000}
              value={lastXJobs ?? ""}
              onChange={(e) => setLastXJobs(e.target.value === "" ? null : Math.min(5000, Math.max(1, Number(e.target.value))))}
              placeholder="—"
              className="w-16 bg-transparent border-b border-amber-500/40 outline-none text-center mono"
              data-testid="cc-hist-lastx"
            />
            <span className="text-muted-foreground">jobs</span>
            <span
              className={`ml-2 mono text-[10px] px-1.5 py-0.5 rounded ${pinCount >= 5000 ? "bg-destructive/20 text-destructive font-bold" : "bg-amber-500/20 text-amber-700 dark:text-amber-300"}`}
              data-testid="cc-hist-count"
              title={pinCount >= 5000 ? "5000-record hard cap hit — narrow your filter" : `Showing ${pinCount} pins`}
            >
              {pinCount} / 5000
            </span>
          </div>
        )}

        {/* iter 73.16 — Stacked date-engine filter builder. Users can
            pile up multiple R/C/A/H/E windows; each becomes a removable
            chip below. AND-logic on the backend. */}
        <button
          type="button"
          onClick={() => setShowDateFilter((v) => !v)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs shadow-sm border transition-colors ${
            dateFilters?.length ? "bg-blue-500 text-white border-blue-600" : "bg-card/95 backdrop-blur border-border hover:bg-muted"
          }`}
          data-testid="cc-datetype-toggle"
          title="Add a date-column filter"
        >
          <CalIcon size={11} />
          {dateFilters?.length ? `Dates (${dateFilters.length})` : "Date filter"}
        </button>
        {showDateFilter && (
          <DateFilterBuilder
            onAdd={(entry) => setDateFilters([...(dateFilters || []), entry])}
            onDone={() => setShowDateFilter(false)}
          />
        )}
        {/* Active date-filter chips (chip == one AND clause) */}
        {(dateFilters || []).map((f, i) => (
          <div
            key={`${f.type}-${i}-${f.start}-${f.end}`}
            className="flex items-center gap-1.5 bg-blue-500 text-white rounded-full px-2.5 py-1.5 text-[11px] shadow-sm"
            data-testid={`cc-datefilter-chip-${i}`}
          >
            <span className="mono font-semibold">{f.type}</span>
            <span className="opacity-90">
              {f.start || "…"} → {f.end || "…"}
            </span>
            <button
              onClick={() => setDateFilters(dateFilters.filter((_, j) => j !== i))}
              className="opacity-80 hover:opacity-100 -mr-1"
              title="Remove this filter"
              data-testid={`cc-datefilter-remove-${i}`}
            ><XIcon size={11} weight="bold" /></button>
          </div>
        ))}

        {/* Driver visibility toggle */}
        <button
          type="button"
          onClick={() => setShowDrivers(!showDrivers)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs shadow-sm border transition-colors ${
            showDrivers ? "bg-card/95 backdrop-blur border-border hover:bg-muted" : "bg-muted text-muted-foreground border-border"
          }`}
          data-testid="cc-driver-visibility-toggle"
          title={showDrivers ? "Hide driver avatars from map" : "Show driver avatars on map"}
        >
          {showDrivers ? <Eye size={11} /> : <EyeSlash size={11} />}
          Drivers
        </button>

        {(lassoSelectedIds?.length ?? 0) > 0 && (
          <div
            className="flex items-center gap-2 bg-amber-500/15 border border-amber-500/40 rounded-full px-2.5 py-1.5 text-xs"
            data-testid="cc-lasso-count"
          >
            <span className="mono font-semibold text-amber-700 dark:text-amber-300">
              {lassoSelectedIds.length} selected
            </span>
            <button
              onClick={clearLasso}
              className="text-amber-700 dark:text-amber-300 hover:opacity-80"
              title="Clear selection"
              data-testid="cc-lasso-clear"
            ><XIcon size={11} weight="bold" /></button>
          </div>
        )}
      </div>

      {/* Bottom-left legend */}
      <div className="absolute bottom-3 left-3 bg-card/95 backdrop-blur border border-border rounded px-2.5 py-1.5 text-[10px] flex items-center gap-3" data-testid="cc-map-legend">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: primary || "#3b82f6" }} /> Job</span>
        <span className="flex items-center gap-1"><Handshake size={10} weight="fill" className="text-purple-500" /> Meeting</span>
        <span className="flex items-center gap-1"><MapPin size={10} weight="fill" className="text-slate-700" /> Driver</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PAGE
// ---------------------------------------------------------------------------
export default function CommandCenter() {
  const { user } = useAuth();
  const orgId = user?.org_id;
  // White-label palette — falls back to the shell blue when the org
  // hasn't set a brand.
  const primary = user?.org?.brand_primary_color || "#3b82f6";

  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [driverQuery, setDriverQuery] = useState("");
  const [dueWithin, setDueWithin] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter 73.14 Phase B — proximity-ranked driver list state.
  const [proximityJob, setProximityJob] = useState(null);
  const [proximityDrivers, setProximityDrivers] = useState([]);
  // iter 73.14 Phase B — lasso multi-select state.
  const [lassoSelectedIds, setLassoSelectedIds] = useState([]);
  // iter 73.15 Phase D — server-persisted layout + org map settings.
  const [layout, setLayout] = useState(null);          // {horizontal:[h1,h2,h3], vertical:[v1,v2]}
  const [mapSettings, setMapSettings] = useState({});  // org map_settings JSONB
  const layoutSaveTimer = useRef(null);
  // iter 73.15 Phase E — Time-Machine / date-engine / viewport / drivers.
  const [mode, setMode] = useState("active");                  // 'active' | 'historical'
  const [histStart, setHistStart] = useState(null);            // YYYY-MM-DD
  const [histEnd, setHistEnd] = useState(null);
  const [lastXJobs, setLastXJobs] = useState(null);
  // iter 73.16 — Stacked timeline filters (AND logic). Each entry:
  //   { type: 'R'|'C'|'A'|'H'|'E', start: 'YYYY-MM-DD'|null, end: same }
  // Backend endpoint accepts `filters=[…json…]`. Chips render below.
  const [dateFilters, setDateFilters] = useState([]);
  const [bounds, setBounds] = useState(null);                  // {ne_lat,ne_lng,sw_lat,sw_lng}
  const [showDrivers, setShowDrivers] = useState(true);

  // iter 73.16 — Predicted next-stop breaches. For each driver whose
  // active stop has a next_stop with a lar_due_at, compute:
  //   remaining_dwell = max(0, expected_duration - (now - arrived_at))
  //   travel_min      = haversine(driver.pos, next.pos) * 1.3 / 35mph
  //   predicted_ETA   = now + remaining_dwell + travel_min
  // If predicted_ETA > next_lar_due_at → BREACH. Return set of job/stop
  // ids that the map should render with a red pulsing ring.
  const nextStopBreachIds = useMemo(() => {
    const s = new Set();
    const now = Date.now();
    const R = 3958.8; // Earth radius in miles
    const hav = (aLat, aLng, bLat, bLng) => {
      const toRad = (x) => (x * Math.PI) / 180;
      const dLat = toRad(bLat - aLat);
      const dLng = toRad(bLng - aLng);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };
    for (const d of drivers) {
      if (!d.next_stop_id || !d.next_lar_due_at) continue;
      if (d.next_lat == null || d.next_lng == null) continue;
      const arrivedMs = d.active_arrived_at ? new Date(d.active_arrived_at).getTime() : now;
      const expectedMin = d.active_expected_duration_minutes ?? 15;
      const remainingDwellMs = Math.max(0, expectedMin * 60000 - (now - arrivedMs));
      let travelMs = 0;
      if (d.last_known_lat != null && d.last_known_lng != null) {
        const miles = hav(d.last_known_lat, d.last_known_lng, d.next_lat, d.next_lng);
        travelMs = (miles * 1.3 / 35) * 3600 * 1000; // detour factor 1.3, avg 35mph
      }
      const predictedEta = now + remainingDwellMs + travelMs;
      const dueMs = new Date(d.next_lar_due_at).getTime();
      if (Number.isFinite(dueMs) && predictedEta > dueMs) {
        // Track by both stop_id AND job_id — the map renders job pins
        // in active mode (matches on job_id) and location pins in
        // historical mode (won't match — that's fine, breaches are a
        // live-view concept).
        if (d.next_stop_id) s.add(d.next_stop_id);
        if (d.next_job_id) s.add(d.next_job_id);
      }
    }
    return s;
  }, [drivers]);

  // Load persisted layout + map settings once.
  useEffect(() => {
    (async () => {
      try {
        const [l, m] = await Promise.all([
          api.get("/command-center/layout").then((r) => r.data).catch(() => ({})),
          api.get("/command-center/map-settings").then((r) => r.data).catch(() => ({})),
        ]);
        setLayout(l && typeof l === "object" ? l : {});
        setMapSettings(m && typeof m === "object" ? m : {});
      } catch { /* fall back to defaults */ }
    })();
  }, []);

  // Debounced write-back — batches drag micro-events into one PUT.
  const persistLayout = useCallback((next) => {
    setLayout(next);
    clearTimeout(layoutSaveTimer.current);
    layoutSaveTimer.current = setTimeout(() => {
      api.put("/command-center/layout", next).catch(() => { /* silent */ });
    }, 600);
  }, []);
  const onHorizontalLayout = useCallback((sizes) => {
    persistLayout({ ...(layout || {}), horizontal: sizes });
  }, [layout, persistLayout]);
  const onVerticalLayout = useCallback((sizes) => {
    persistLayout({ ...(layout || {}), vertical: sizes });
  }, [layout, persistLayout]);

  const fetchJobs = useCallback(async () => {
    try {
      // iter 73.15 Phase E — expanded query params for the 5-point date
      // engine, time-machine mode, and viewport bounds. Historical mode
      // rides on the same /jobs endpoint (jobs still power the left
      // Queue); the /locations endpoint feeds the *map pins* directly
      // and is invoked separately below.
      const p = new URLSearchParams();
      if (dueWithin != null) p.set("due_within_days", String(dueWithin));
      const url = p.toString() ? `/command-center/jobs?${p}` : "/command-center/jobs";
      const r = await api.get(url);
      setJobs(Array.isArray(r.data) ? r.data : []);
    } catch { setJobs([]); }
  }, [dueWithin]);

  const fetchDrivers = useCallback(async () => {
    try {
      const r = await api.get("/command-center/drivers");
      setDrivers(Array.isArray(r.data) ? r.data : []);
    } catch { setDrivers([]); }
  }, []);

  // iter 73.15 Phase E — Entity-centric location pins.
  // In *active* mode we still show pins derived from /command-center/jobs
  // (each job → one pin at its service_location), which keeps the map
  // reactive to per-job selection. In *historical* mode we switch to
  // /command-center/locations so the map can pull up to 5000 records
  // from the archive without loading every job's full detail.
  const [locationPins, setLocationPins] = useState([]);
  const fetchLocationPins = useCallback(async () => {
    if (mode !== "historical") { setLocationPins([]); return; }
    try {
      const p = new URLSearchParams();
      p.set("mode", "historical");
      if (histStart) p.set("start", histStart);
      if (histEnd) p.set("end", histEnd);
      if (lastXJobs) p.set("last_x_jobs", String(lastXJobs));
      // iter 73.16 — send stacked filters
      if (dateFilters?.length) {
        p.set("filters", JSON.stringify(dateFilters));
      }
      if (bounds) {
        p.set("bounds_ne_lat", String(bounds.ne_lat));
        p.set("bounds_ne_lng", String(bounds.ne_lng));
        p.set("bounds_sw_lat", String(bounds.sw_lat));
        p.set("bounds_sw_lng", String(bounds.sw_lng));
      }
      const r = await api.get(`/command-center/locations?${p}`);
      setLocationPins(Array.isArray(r.data) ? r.data : []);
    } catch { setLocationPins([]); }
  }, [mode, histStart, histEnd, lastXJobs, dateFilters, bounds]);

  // Phase B — when a job is selected, fetch drivers ranked by distance
  // from that job. Falls back gracefully (404 = job has no location).
  const fetchProximity = useCallback(async (job) => {
    if (!job?.id) return;
    try {
      const r = await api.get(`/command-center/drivers/proximity?job_id=${encodeURIComponent(job.id)}`);
      setProximityDrivers(Array.isArray(r.data) ? r.data : []);
      setProximityJob(job);
    } catch {
      // job has no lat/lng — silently stay in status-ranked mode.
      setProximityDrivers([]);
      setProximityJob(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchJobs(), fetchDrivers()]);
      setLoading(false);
    })();
  }, [fetchJobs, fetchDrivers]);

  // iter 73.15 Phase E — historical/date/bounds refetch.
  useEffect(() => { fetchLocationPins(); }, [fetchLocationPins]);

  // Adapter — the map consumes `mapJobs`, an array with lat/lng.
  // In active mode this is just `jobs`. In historical mode it's the
  // aggregated /locations rows (each rendered as a single job-like pin).
  const mapJobs = useMemo(() => {
    if (mode === "active") return jobs;
    return locationPins.map((l) => ({
      id: l.id,
      job_number: l.name,
      status_name: `${l.job_count} jobs`,
      is_setup_meeting: (l.meeting_count || 0) > 0,
      latitude: l.latitude,
      longitude: l.longitude,
      location_name: l.name,
      address_line1: l.address_line1,
      city: l.city, state: l.state,
      due_date: l.next_due,
    }));
  }, [mode, jobs, locationPins]);

  // Whenever selectedJob changes (and has a location), re-run proximity.
  useEffect(() => {
    if (selectedJob && selectedJob.latitude != null && selectedJob.longitude != null) {
      fetchProximity(selectedJob);
    } else {
      setProximityJob(null);
      setProximityDrivers([]);
    }
  }, [selectedJob, fetchProximity]);

  const exitProximity = () => { setProximityJob(null); setProximityDrivers([]); };
  // iter 73.14 Phase C — Job → RouteBuilder bus. Job Queue rows push
  // jobs into this queue; RouteBuilder pops them into stops and calls
  // onConsumed() so the same job isn't added twice on re-render.
  const [routeBuilderInbox, setRouteBuilderInbox] = useState([]);
  const pushToRouteBuilder = useCallback((job) => {
    setRouteBuilderInbox((prev) => [...prev, job]);
  }, []);
  // iter 73.15 Phase F — bulk add from JobQueue multi-select.
  const bulkPushToRouteBuilder = useCallback((rows) => {
    if (!rows?.length) return;
    setRouteBuilderInbox((prev) => [...prev, ...rows]);
  }, []);
  const clearRouteBuilderInbox = useCallback(() => setRouteBuilderInbox([]), []);

  // iter 73.15 Phase F — Global location search + Ghost Pin. The pin
  // persists on the map until the user clears it manually.
  const [ghostPin, setGhostPin] = useState(null);   // {lat, lng, label} | null

  // Realtime — on any jobs/users/locations change in our org, refetch.
  // Debounced (250 ms) so a burst of updates coalesces into one round-trip.
  useEffect(() => {
    if (!supabase || !orgId) return;
    let t;
    const debounced = (fn) => { clearTimeout(t); t = setTimeout(fn, 250); };
    const ch = supabase
      .channel(`cc:${orgId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "jobs",      filter: `org_id=eq.${orgId}` }, () => debounced(fetchJobs))
      .on("postgres_changes", { event: "*", schema: "public", table: "locations", filter: `org_id=eq.${orgId}` }, () => debounced(fetchJobs))
      .on("postgres_changes", { event: "*", schema: "public", table: "users",     filter: `org_id=eq.${orgId}` }, () => debounced(fetchDrivers))
      .subscribe();
    return () => { supabase.removeChannel(ch); clearTimeout(t); };
  }, [orgId, fetchJobs, fetchDrivers]);

  return (
    <div className="h-full w-full flex flex-col" data-testid="command-center">
      <div className="h-9 px-4 border-b border-border flex items-center gap-3 bg-card/80 backdrop-blur flex-shrink-0">
        <MapTrifold size={14} style={{ color: primary }} />
        <div className="text-xs font-semibold">Command Center</div>
        <div className="text-[10px] text-muted-foreground mono">
          {jobs.length} jobs · {drivers.length} servers
        </div>
        {loading && <ArrowsClockwise size={11} className="animate-spin text-muted-foreground" />}
      </div>

      <div className="flex-1 min-h-0">
        <PanelGroup
          direction="horizontal"
          autoSaveId="cc-layout"
          className="h-full"
          onLayout={onHorizontalLayout}
        >
          <Panel
            defaultSize={layout?.horizontal?.[0] ?? 22}
            minSize={15}
            maxSize={40}
            className="border-r border-border"
          >
            <JobQueue
              jobs={jobs}
              selectedId={selectedJob?.id}
              onSelect={setSelectedJob}
              onAddToRoute={pushToRouteBuilder}
              onBulkAddToRoute={bulkPushToRouteBuilder}
              primary={primary}
              mapSettings={mapSettings}
            />
          </Panel>
          <PanelResizeHandle className="w-1 bg-border hover:bg-primary transition-colors" data-testid="cc-resize-left" />
          <Panel defaultSize={layout?.horizontal?.[1] ?? 56} minSize={30}>
            <MapPanel
              jobs={mapJobs}
              drivers={drivers}
              selectedId={selectedJob?.id}
              onSelectJob={setSelectedJob}
              dueWithin={dueWithin}
              setDueWithin={setDueWithin}
              primary={primary}
              onLassoSelect={setLassoSelectedIds}
              lassoSelectedIds={lassoSelectedIds}
              onClearLasso={() => setLassoSelectedIds([])}
              mapSettings={mapSettings}
              mode={mode} setMode={setMode}
              dateFilters={dateFilters} setDateFilters={setDateFilters}
              histStart={histStart} setHistStart={setHistStart}
              histEnd={histEnd} setHistEnd={setHistEnd}
              lastXJobs={lastXJobs} setLastXJobs={setLastXJobs}
              showDrivers={showDrivers} setShowDrivers={setShowDrivers}
              onBoundsChanged={setBounds}
              pinCount={mapJobs.length}
              ghostPin={ghostPin}
              onSetGhostPin={setGhostPin}
              nextStopBreachIds={nextStopBreachIds}
            />
          </Panel>
          <PanelResizeHandle className="w-1 bg-border hover:bg-primary transition-colors" data-testid="cc-resize-right" />
          <Panel
            defaultSize={layout?.horizontal?.[2] ?? 22}
            minSize={15}
            maxSize={40}
            className="border-l border-border"
          >
            <PanelGroup
              direction="vertical"
              autoSaveId="cc-right-layout"
              onLayout={onVerticalLayout}
            >
              <Panel defaultSize={layout?.vertical?.[0] ?? 55} minSize={20}>
                <DriverList
                  drivers={proximityJob ? proximityDrivers : drivers}
                  query={driverQuery}
                  setQuery={setDriverQuery}
                  primary={primary}
                  proximityJob={proximityJob}
                  onExitProximity={exitProximity}
                />
              </Panel>
              <PanelResizeHandle className="h-1 bg-border hover:bg-primary transition-colors" data-testid="cc-resize-right-v" />
              <Panel defaultSize={layout?.vertical?.[1] ?? 45} minSize={20}>
                <RouteBuilder
                  drivers={drivers}
                  primary={primary}
                  addedJobs={routeBuilderInbox}
                  onConsumed={clearRouteBuilderInbox}
                />
              </Panel>
            </PanelGroup>
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
}
